import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { telecallerService } from '../services/telecallerService';
import {
  Telecaller,
  CreateTelecallerDTO,
  UpdateTelecallerDTO,
} from '../types/telecaller';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

interface City {
  id: string;
  name: string;
  code: string;
  state: string;
  isActive: boolean;
  isOperational: boolean;
}

const TelecallerManagement: React.FC = () => {
  const [telecallers, setTelecallers] = useState<Telecaller[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPerformanceDialog, setOpenPerformanceDialog] = useState(false);
  const [editingTelecaller, setEditingTelecaller] = useState<Telecaller | null>(null);
  const [selectedTelecaller, setSelectedTelecaller] = useState<Telecaller | null>(null);
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('true'); // Default to showing active telecallers
  const [cityFilter, setCityFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Form data
  const [formData, setFormData] = useState<CreateTelecallerDTO>({
    name: '',
    phone: '',
    email: '',
    employeeId: '',
    assignedCities: [],
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    phone?: string;
    email?: string;
    assignedCities?: string;
  }>({});

  // Validation functions
  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const errors: typeof formErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      errors.phone = 'Invalid phone number (10 digits, starting with 6-9)';
    }

    // Email validation
    if (formData.email && !validateEmail(formData.email)) {
      errors.email = 'Invalid email format';
    }

    // Assigned cities validation
    if (formData.assignedCities.length === 0) {
      errors.assignedCities = 'At least one city must be assigned';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Load cities from vehicle service
  const loadCities = useCallback(async () => {
    try {
      setLoadingCities(true);
      const response = await axios.get(`${API_BASE_URL}/cities`, {
        params: { isActive: true, isOperational: true },
      });

      if (response.data.success && response.data.data) {
        setCities(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error loading cities:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load cities',
        severity: 'error',
      });
    } finally {
      setLoadingCities(false);
    }
  }, []);

  const loadTelecallers = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);

      console.log('📡 Loading telecallers...', {
        page: page + 1,
        limit: rowsPerPage,
        filters: { search: searchTerm, isActive: activeFilter, city: cityFilter },
      });

      const response = await telecallerService.getTelecallers({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        isActive: activeFilter === '' ? undefined : activeFilter === 'true',
        city: cityFilter || undefined,
      });

      console.log('✅ Telecallers loaded:', response);

      setTelecallers(response.data || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (error) {
      console.error('❌ Error loading telecallers:', error);
      setSnackbar({
        open: true,
        message: `Failed to load telecallers: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, activeFilter, cityFilter]);

  useEffect(() => {
    loadCities();
    loadTelecallers();
  }, [loadCities, loadTelecallers]);

  const handleOpenDialog = (telecaller?: Telecaller) => {
    if (telecaller) {
      setEditingTelecaller(telecaller);
      setFormData({
        name: telecaller.name,
        phone: telecaller.phone,
        email: telecaller.email || '',
        employeeId: telecaller.employeeId || '',
        assignedCities: telecaller.assignedCities || [],
      });
    } else {
      setEditingTelecaller(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        employeeId: '',
        assignedCities: [],
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTelecaller(null);
    setFormErrors({});
  };

  const handleSaveTelecaller = async () => {
    // Validate form
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors',
        severity: 'error',
      });
      return;
    }

    try {
      setLoading(true);

      // Clean up form data - remove empty strings
      const cleanedData: CreateTelecallerDTO = {
        name: formData.name,
        phone: formData.phone,
        assignedCities: formData.assignedCities,
        ...(formData.email?.trim() && { email: formData.email.trim() }),
        ...(formData.employeeId?.trim() && { employeeId: formData.employeeId.trim() }),
      };

      console.log('💾 Saving telecaller:', cleanedData);

      if (editingTelecaller) {
        await telecallerService.updateTelecaller(editingTelecaller.id, cleanedData as UpdateTelecallerDTO);
        setSnackbar({
          open: true,
          message: 'Telecaller updated successfully',
          severity: 'success',
        });
      } else {
        await telecallerService.createTelecaller(cleanedData);
        setSnackbar({
          open: true,
          message: 'Telecaller created successfully',
          severity: 'success',
        });
      }
      handleCloseDialog();
      loadTelecallers(false);
    } catch (error: any) {
      console.error('❌ Error saving telecaller:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save telecaller';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateTelecaller = async (telecaller: Telecaller) => {
    try {
      setLoading(true);
      await telecallerService.deactivateTelecaller(telecaller.id);
      setSnackbar({
        open: true,
        message: 'Telecaller deactivated successfully',
        severity: 'success',
      });
      loadTelecallers(false);
    } catch (error) {
      console.error('❌ Error deactivating telecaller:', error);
      setSnackbar({
        open: true,
        message: `Failed to deactivate telecaller: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewPerformance = async (telecaller: Telecaller) => {
    try {
      setSelectedTelecaller(telecaller);
      setOpenPerformanceDialog(true);
      const performance = await telecallerService.getTelecallerPerformance(telecaller.id);
      setPerformanceData(performance.data);
    } catch (error) {
      console.error('❌ Error loading performance:', error);
      setSnackbar({
        open: true,
        message: `Failed to load performance: ${(error as Error).message}`,
        severity: 'error',
      });
    }
  };

  const handleCityChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      assignedCities: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Telecaller Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadTelecallers()}
          >
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add Telecaller
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="Search by name, phone, or employee ID"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={activeFilter}
                  label="Status"
                  onChange={(e) => setActiveFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select
                  value={cityFilter}
                  label="City"
                  onChange={(e) => setCityFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {loadingCities ? (
                    <MenuItem disabled>Loading cities...</MenuItem>
                  ) : (
                    cities.map((city) => (
                      <MenuItem key={city.id} value={city.name}>
                        {city.name} ({city.code})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setActiveFilter('true'); // Reset to show active telecallers
                  setCityFilter('');
                }}
                sx={{ height: '56px' }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Telecaller Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Employee ID</TableCell>
                <TableCell>Assigned Cities</TableCell>
                <TableCell align="right">Total Leads</TableCell>
                <TableCell align="right">Converted Leads</TableCell>
                <TableCell align="right">Conversion Rate</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : telecallers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No telecallers found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                telecallers.map((telecaller) => (
                  <TableRow key={telecaller.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {telecaller.telecallerCode}
                      </Typography>
                    </TableCell>
                    <TableCell>{telecaller.name}</TableCell>
                    <TableCell>{telecaller.phone}</TableCell>
                    <TableCell>{telecaller.email}</TableCell>
                    <TableCell>{telecaller.employeeId}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {telecaller.assignedCities?.map((city) => (
                          <Chip key={city} label={city} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{telecaller.totalLeads}</TableCell>
                    <TableCell align="right">{telecaller.convertedLeads}</TableCell>
                    <TableCell align="right">
                      {(telecaller.conversionRate * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={telecaller.isActive ? 'Active' : 'Inactive'}
                        color={telecaller.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View Performance">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewPerformance(telecaller)}
                          >
                            <AssessmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(telecaller)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {telecaller.isActive ? (
                          <Tooltip title="Deactivate">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeactivateTelecaller(telecaller)}
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Activate">
                            <IconButton size="small" color="success" disabled>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingTelecaller ? 'Edit Telecaller' : 'Add New Telecaller'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (formErrors.name) {
                    setFormErrors({ ...formErrors, name: undefined });
                  }
                }}
                error={!!formErrors.name}
                helperText={formErrors.name || 'Telecaller full name (minimum 3 characters)'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Phone"
                value={formData.phone}
                onChange={(e) => {
                  setFormData({ ...formData, phone: e.target.value });
                  if (formErrors.phone) {
                    setFormErrors({ ...formErrors, phone: undefined });
                  }
                }}
                error={!!formErrors.phone}
                helperText={formErrors.phone || 'Mobile number (10 digits, starts with 6-9)'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (formErrors.email) {
                    setFormErrors({ ...formErrors, email: undefined });
                  }
                }}
                error={!!formErrors.email}
                helperText={formErrors.email || 'Email address (optional)'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employee ID"
                value={formData.employeeId}
                onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                helperText="Internal employee identifier (optional)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!formErrors.assignedCities}>
                <InputLabel>Assigned Cities *</InputLabel>
                <Select
                  multiple
                  value={formData.assignedCities}
                  onChange={handleCityChange}
                  input={<OutlinedInput label="Assigned Cities *" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {loadingCities ? (
                    <MenuItem disabled>Loading cities...</MenuItem>
                  ) : cities.length === 0 ? (
                    <MenuItem disabled>No cities available</MenuItem>
                  ) : (
                    cities.map((city) => (
                      <MenuItem key={city.id} value={city.name}>
                        {city.name} ({city.code})
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formErrors.assignedCities && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.assignedCities}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveTelecaller} variant="contained" disabled={loading}>
            {editingTelecaller ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Performance Dialog */}
      <Dialog
        open={openPerformanceDialog}
        onClose={() => setOpenPerformanceDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Performance - {selectedTelecaller?.name}</DialogTitle>
        <DialogContent>
          {performanceData ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Total Leads</Typography>
                    <Typography variant="h4">{performanceData.totalLeads}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Converted Leads</Typography>
                    <Typography variant="h4">{performanceData.convertedLeads || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Conversion Rate</Typography>
                    <Typography variant="h4">
                      {(performanceData.conversionRate * 100).toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Conversions by Stage
                    </Typography>
                    <Stack spacing={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Contacted:</Typography>
                        <Typography fontWeight="bold">
                          {performanceData.conversionsByStage?.contacted || 0}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Registered:</Typography>
                        <Typography fontWeight="bold">
                          {performanceData.conversionsByStage?.registered || 0}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>KYC Verified:</Typography>
                        <Typography fontWeight="bold">
                          {performanceData.conversionsByStage?.kycVerified || 0}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>First Delivery:</Typography>
                        <Typography fontWeight="bold">
                          {performanceData.conversionsByStage?.firstDelivery || 0}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Retained:</Typography>
                        <Typography fontWeight="bold">
                          {performanceData.conversionsByStage?.retained || 0}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Performing Cities
                    </Typography>
                    <Stack spacing={1}>
                      {performanceData.topPerformingCities?.map((city: any, index: number) => (
                        <Box key={index} display="flex" justifyContent="space-between">
                          <Typography>{city.city}:</Typography>
                          <Typography fontWeight="bold">{city.count} leads</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPerformanceDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TelecallerManagement;
